import {
  idref,
  uniqueId,
} from '../../utilities/dom.js';

import {
  State,
} from './ToggleButton.constants.js';

import {
  getMediaQuery,
} from './ToggleButton.helpers.js';

export class ToggleButton extends HTMLElement {
  /** @param {string} tagName */
  static define(tagName = 'toggle-button') {
    if ('customElements' in window) {
      customElements.define(tagName, ToggleButton);
    }
  }

  /** @returns {?HTMLElement} */
  get targetElement() {
    return this.#targetElement;
  }

  /** @param {?HTMLElement} value */
  set targetElement(value) {
    this.#targetElement = value;
  }

  /** @returns {?string} */
  get target() {
    return this.getAttribute('target');
  }

  /** @param {?string} value */
  set target(value) {
    if (value) {
      this.setAttribute('target', value);
    } else {
      this.removeAttribute('target');
    }
  }

  /** @returns {?string} */
  get action() {
    return this.getAttribute('action') ?? 'toggle';
  }

  /** @param {?string} value */
  set action(value) {
    if (value) {
      this.setAttribute('action', value);
    } else {
      this.removeAttribute('action');
    }
  }

  /** @returns {boolean} */
  get open() {
    return !this.#targetElement?.hasAttribute('hidden');
  }

  /** @returns {?string} */
  get openLabel() {
    return this.getAttribute('open-label');
  }

  /** @param {?string} value */
  set openLabel(value) {
    if (value) {
      this.setAttribute('open-label', value);
    } else {
      this.removeAttribute('open-label');
    }
  }

  /** @returns {?string} */
  get closeLabel() {
    return this.getAttribute('close-label');
  }

  /** @param {?string} value */
  set closeLabel(value) {
    if (value) {
      this.setAttribute('close-label', value);
    } else {
      this.removeAttribute('close-label');
    }
  }

  /** @returns {?string} */
  get forceOpen() {
    return this.getAttribute('force-open');
  }

  /** @param {?string} value */
  set forceOpen(value) {
    if (value) {
      this.setAttribute('force-open', value);
    } else {
      this.removeAttribute('force-open');
    }
  }

  /** @returns {?string} */
  get forceClose() {
    return this.getAttribute('force-close');
  }

  /** @param {?string} value */
  set forceClose(value) {
    if (value) {
      this.setAttribute('force-close', value);
    } else {
      this.removeAttribute('force-close');
    }
  }

  /** @returns {?string} */
  get closeOnEscape() {
    return this.getAttribute('close-on-escape');
  }

  /** @param {?string} value */
  set closeOnEscape(value) {
    if (value) {
      this.setAttribute('close-on-escape', value);
    } else {
      this.removeAttribute('close-on-escape');
    }
  }

  /** @returns {?string} */
  get closeOnClickOutside() {
    return this.getAttribute('close-on-click-outside');
  }

  /** @param {?string} value */
  set closeOnClickOutside(value) {
    if (value) {
      this.setAttribute('close-on-click-outside', value);
    } else {
      this.removeAttribute('close-on-click-outside');
    }
  }

  /** @type {?HTMLButtonElement} */
  #buttonElement = null;

  /** @type {?HTMLElement} */
  #targetElement = null;

  /** @type {?MediaQueryList} */
  #forceOpenQuery = null;

  /** @type {?MediaQueryList} */
  #forceCloseQuery = null;

  constructor() {
    super();

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleToggle = this.handleToggle.bind(this);
    this.handleForceOpenChange = this.handleForceOpenChange.bind(this);
    this.handleForceCloseChange = this.handleForceCloseChange.bind(this);
  }

  /**
   * @returns {void}
   */
  connectedCallback() {
    this.#buttonElement = /** @type {?HTMLButtonElement} */ (
      this.querySelector('button')
    );

    this.#targetElement = /** @type {?HTMLElement} */ (
      this.target ? idref(this.target) : this.nextElementSibling
    );

    if (this.#buttonElement === null) {
      throw new Error(`<${this.localName}> must contain a <button> element`);
    }

    if (this.#targetElement === null) {
      throw new Error(`<${this.localName}> must reference a target element`);
    }

    this.#targetElement.id ||= uniqueId(`${this.#targetElement.localName}-`);
    this.#buttonElement.setAttribute('aria-controls', this.#targetElement.id);

    this.#forceOpenQuery = getMediaQuery(this.getAttributeValue('force-open'));
    this.#forceCloseQuery = getMediaQuery(this.getAttributeValue('force-close'));

    if (this.#forceOpenQuery.matches && this.#forceCloseQuery.matches) {
      this.toggleTarget();
    } else if (this.#forceOpenQuery.matches) {
      this.showTarget();
    } else if (this.#forceCloseQuery.matches) {
      this.hideTarget();
    } else {
      this.#setState(this.open);
    }

    this.#buttonElement.addEventListener('click', this.handleClick);
    this.#buttonElement.addEventListener('keydown', this.handleKeydown);

    this.#targetElement.addEventListener('toggle', this.handleToggle);
    this.#targetElement.addEventListener('keydown', this.handleKeydown);

    this.#forceOpenQuery.addEventListener('change', this.handleForceOpenChange);
    this.#forceCloseQuery.addEventListener('change', this.handleForceCloseChange);

    document.addEventListener('click', this.handleClickOutside);
  }

  /**
   * @returns {void}
   */
  disconnectedCallback() {
    document.removeEventListener('click', this.handleClickOutside);

    this.#forceOpenQuery?.removeEventListener('change', this.handleForceOpenChange);
    this.#forceCloseQuery?.removeEventListener('change', this.handleForceCloseChange);

    this.#targetElement?.removeEventListener('keydown', this.handleKeydown);
    this.#targetElement?.removeEventListener('toggle', this.handleToggle);

    this.#buttonElement?.removeEventListener('keydown', this.handleKeydown);
    this.#buttonElement?.removeEventListener('click', this.handleClick);

    this.#buttonElement = null;
    this.#targetElement = null;
  }

  /**
   * @param {string} name
   * @returns {string|boolean}
   */
  getAttributeValue(name) {
    const value = this.getAttribute(name);

    if (value === undefined || value === '') {
      return true;
    }

    if (value) {
      return value;
    }

    return false;
  }

  /**
   * @param {?boolean} [force]
   * @returns {boolean}
   */
  toggleTarget(force = null) {
    const oldValue = this.open;
    const newValue = (force != null) ? !!force : !oldValue;

    if (oldValue === newValue) {
      return oldValue;
    }

    this.#targetElement?.toggleAttribute('hidden', !newValue);

    this.#targetElement?.dispatchEvent(new CustomEvent('toggle', {
      detail: {
        source: this.#buttonElement,
        oldState: oldValue ? State.Open : State.Closed,
        newState: newValue ? State.Open : State.Closed,
      },
    }));

    return newValue;
  }

  /**
   * @returns {void}
   */
  showTarget() {
    this.toggleTarget(true);
  }

  /**
   * @returns {void}
   */
  hideTarget() {
    this.toggleTarget(false);
  }

  /**
   * @returns {void}
   */
  handleClick() {
    const {action} = this;

    if (action === 'show') {
      this.showTarget();
    } else if (action === 'hide') {
      this.hideTarget();
    } else {
      this.toggleTarget();
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (! this.open) {
      return;
    }

    const {key} = event;

    if (key !== 'Escape') {
      return;
    }

    const {matches} = getMediaQuery(this.getAttributeValue('close-on-escape'));

    if (! matches) {
      return;
    }

    this.hideTarget();
    this.#buttonElement?.focus();

    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * @param {Event} event
   */
  handleToggle(event) {
    const toggleEvent = /** @type {CustomEvent} */ (event);

    this.#setState(
      toggleEvent.detail?.newState === State.Open,
    );
  }

  /**
   * @param {Event} event
   */
  handleClickOutside(event) {
    if (! this.open) {
      return;
    }

    const target = /** @type {?HTMLElement} */ (event.target);

    if (! target) {
      return;
    }

    const isClickInside = this.contains(target) || this.#targetElement?.contains(target);

    if (isClickInside) {
      return;
    }

    const {matches} = getMediaQuery(this.getAttributeValue('close-on-click-outside'));

    if (! matches) {
      return;
    }

    this.hideTarget();
    event.preventDefault();
  }

  /**
   * @param {MediaQueryListEvent} event
   */
  handleForceOpenChange(event) {
    this.toggleTarget(event.matches);
  }

  /**
   * @param {MediaQueryListEvent} event
   */
  handleForceCloseChange(event) {
    this.toggleTarget(!event.matches);
  }

  /**
   * @param {boolean} open
   */
  #setState(open) {
    const { openLabel, closeLabel } = this;

    if (openLabel && closeLabel) {
      this.#buttonElement?.setAttribute('aria-label', open ? openLabel : closeLabel);
    }

    if (! this.#targetElement?.contains(this.#buttonElement)) {
      this.#buttonElement?.setAttribute('aria-expanded', open ? 'true' : 'false');
    } else {
      this.#buttonElement?.removeAttribute('aria-expanded');
    }
  }

}